import { createRouter, type RouterHistory } from '@tanstack/react-router';
import { DefaultErrorComponent } from './components/router/DefaultErrorComponent';
import { DefaultNotFoundComponent } from './components/router/DefaultNotFoundComponent';
import { DefaultPendingComponent } from './components/router/DefaultPendingComponent';
import type { LocalisedPaths } from './entities/urls/localised-path';
import { createLocalisedUrlRewrite } from './entities/urls/localised-url-rewrite';
import { createUrlLanguage, type UrlLanguage } from './entities/urls/url-language';
import { routeTree as defaultRouteTree } from './routeTree.gen';

/** What every route's `beforeLoad` and `loader` get as `context`. */
export type AppRouterContext = {
  /** The language of the address, as `router.urlLanguage` (for routes whose content depends on it). */
  urlLanguage: UrlLanguage;
};

export type AppRouterOptions = {
  /** Browser history when omitted; tests pass a memory history. */
  history?: RouterHistory;
  /** Tests only: another route tree of the same shape. */
  routeTree?: typeof defaultRouteTree;
  /** Tests only: mappings over another slug table (e.g. with legacy slugs). */
  paths?: LocalisedPaths;
  isServer?: boolean;
  /**
   * The site's origin (`https://host`). The browser's own when omitted; a server render passes the site's, as the
   * router's fallback there is `http://localhost` (NotFoundPage shows the failed address with its host).
   */
  origin?: string;
};

/**
 * The app's router. Its `rewrite` maps the public `/<lang>/<localised words and slugs>` addresses to the
 * internal English paths of the route tree and back (src/entities/urls/localised-url-rewrite.ts), so routes
 * and `to="/…"` links stay in English. The language of the current address is `router.urlLanguage`.
 */
export const createAppRouter = ({
  history,
  routeTree = defaultRouteTree,
  paths,
  isServer,
  origin,
}: AppRouterOptions = {}) => {
  const urlLanguage = createUrlLanguage();
  const router = createRouter({
    routeTree,
    history,
    isServer,
    origin,
    rewrite: createLocalisedUrlRewrite(urlLanguage, paths),
    context: { urlLanguage },
    defaultErrorComponent: DefaultErrorComponent,
    defaultNotFoundComponent: DefaultNotFoundComponent,
    defaultPendingComponent: DefaultPendingComponent,
  });

  // The router caches the location a <Link> with an absolute `to` builds and doesn't expect the output
  // rewrite to change: without this, links mounted outside the page (header, footer) would keep the
  // previous language's hrefs after the address changes language. Same reset the router does in update().
  urlLanguage.subscribe(() => {
    (router as unknown as { staticLocations: WeakMap<object, unknown> }).staticLocations = new WeakMap();
  });

  return Object.assign(router, { urlLanguage: urlLanguage as UrlLanguage });
};

export type AppRouter = ReturnType<typeof createAppRouter>;

declare module '@tanstack/react-router' {
  interface Register {
    router: AppRouter;
  }
}
